import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Bookmark, X } from 'lucide-react';
import { promptsApi } from '@/lib/prompts';
import { useCurrentUser } from '@/hooks/useAuth';

/**
 * Team prompt library surface for an agent page. Shows the team's saved prompts
 * as clickable chips (never a blank box), and lets the user save the current
 * input as a reusable team prompt. Creator-scoped delete.
 */
export function SavedPrompts({
  wsId,
  agentKind = 'ask',
  onPick,
  currentText,
}: {
  wsId: string;
  agentKind?: string;
  onPick: (body: string) => void;
  currentText?: string;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: me } = useCurrentUser();
  const { data: prompts = [] } = useQuery({
    queryKey: ['prompts', wsId, agentKind],
    queryFn: () => promptsApi.list(wsId, agentKind),
    enabled: !!wsId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['prompts', wsId, agentKind] });
  const save = useMutation({
    mutationFn: () => {
      const body = (currentText ?? '').trim();
      const title = body.length > 60 ? `${body.slice(0, 59)}…` : body;
      return promptsApi.create(wsId, { title, body, agentKind });
    },
    onSuccess: invalidate,
  });
  const del = useMutation({
    mutationFn: (id: string) => promptsApi.remove(wsId, id),
    onSuccess: invalidate,
  });

  const canSave = !!currentText && currentText.trim().length > 0;
  if (prompts.length === 0 && !canSave) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] uppercase tracking-[0.14em] text-ink-400 font-semibold inline-flex items-center gap-1">
        <Bookmark size={12} /> {t('prompts.label', { defaultValue: 'Team prompts' })}
      </span>
      {prompts.map((p) => (
        <span
          key={p.id}
          className="group inline-flex items-center gap-1 rounded-full border border-citation-200 bg-citation-50/60 text-citation-700 pl-3 pr-1.5 py-1 text-[12.5px]"
        >
          <button onClick={() => onPick(p.body)} className="hover:underline max-w-[220px] truncate" title={p.body}>
            {p.title}
          </button>
          {(!p.createdBy || p.createdBy === me?.id) && (
            <button
              onClick={() => del.mutate(p.id)}
              disabled={del.isPending}
              aria-label={t('common.delete', { defaultValue: 'Delete' }) as string}
              className="opacity-40 group-hover:opacity-100 hover:text-warn-600 transition"
            >
              <X size={12} />
            </button>
          )}
        </span>
      ))}
      {canSave && (
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="rounded-full border border-dashed border-ink-300 text-ink-500 px-3 py-1 text-[12.5px] hover:border-accent-400 hover:text-accent-600 transition disabled:opacity-50"
        >
          {save.isPending ? t('prompts.saving', { defaultValue: 'Saving…' }) : `+ ${t('prompts.save', { defaultValue: 'Save prompt' })}`}
        </button>
      )}
    </div>
  );
}
